import { faChevronRight } from "@fullscript/pro-regular-svg-icons";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import { Typography, Spacer, Button, FloatingLabelInput, useToast } from "@aviary";
import { LoginOptions } from "@shared/components/LoginOptions/LoginOptions";
import { useAnalyticsLogger } from "@shared/hooks/useAnalyticsLogger/useAnalyticsLogger";
import { useBreakpoints } from "@shared/hooks/useBreakpoints/useBreakpoints";
import { FontAwesomeIcon } from "@shared/react-fontawesome/react-fontawesome";
import type { SwiftFillSteps } from "@shared/types/SwiftFillSteps";
import type { AuthUserSignInInput } from "@shared/types/graphqlGenerated";
import { gRecaptchaExecute } from "@shared/utils/gRecaptchaExecute/gRecaptchaExecute";
import { useUserSignInUnauthenticatedSharedMutation } from "@unauthenticated/shared/data/mutations/UserSignIn.mutation.generated";
import { l } from "@unauthenticated/signupSignin/locales/i18n";

import { JourneyMagicLinkButton } from "../JourneyMagicLinkButton/JourneyMagicLinkButton";

import * as styles from "./JourneyLoginForm.styles";

// Form element ID constants
const FORM_IDS = {
  EMAIL: "email",
  PASSWORD: "password",
} as const;

interface LoginState {
  email: string;
  zipCode: string;
  nextStep: SwiftFillSteps;
}

interface JourneyLoginFormProps {
  onLoginSuccess?: () => void;
  onMagicLinkClick?: () => void;
  email?: string;
  storeSlug?: string;
  storeId?: string;
  journeyPackageId?: string;
  loginState?: LoginState;
}

const JourneyLoginForm = ({
  onLoginSuccess,
  onMagicLinkClick,
  email: initialEmail,
  storeSlug,
  storeId,
  journeyPackageId,
  loginState,
}: JourneyLoginFormProps) => {
  const { t } = useTranslation();
  const { tablet } = useBreakpoints();
  const { makeToast } = useToast();
  const logAnalyticsEvent = useAnalyticsLogger();
  const [email, setEmail] = useState(initialEmail || "");
  const [password, setPassword] = useState("");

  const isMobile = tablet.lessThan;

  // Build additionalAttributes like Luis's implementation
  const additionalAttributes =
    storeSlug && journeyPackageId
      ? {
          redirectPath: (() => {
            const baseUrl = `/r/checkout/${storeSlug}/journey/${journeyPackageId}`;

            // Include login state in redirect URL so it can be restored when user comes back
            if (loginState) {
              const params = new URLSearchParams({
                email: loginState.email,
                zipCode: loginState.zipCode,
                nextStep: loginState.nextStep,
              });

              return `${baseUrl}?${params.toString()}`;
            }

            return baseUrl;
          })(),
        }
      : undefined;

  const [userSignIn, { loading: isSignInLoading }] = useUserSignInUnauthenticatedSharedMutation({
    onCompleted: data => {
      const signInResult = data?.auth?.userSignIn;

      if (signInResult?.errors?.message) {
        makeToast("error", signInResult.errors.message);

        return;
      }

      // Handle 2FA if required
      if (signInResult?.twoFactorAuthenticationToken) {
        // TODO: Handle 2FA flow - for now just show error
        makeToast("error", t(l.journeyCheckout.login.errors.twoFactorNotSupported));

        return;
      }

      // Let backend handle the redirect
      if (signInResult?.redirectPath) {
        window.location.assign(signInResult.redirectPath);

        return;
      }

      // If no redirect path, call the success callback
      onLoginSuccess?.();
    },
    onError: error => {
      makeToast("error", error?.message || t(l.journeyCheckout.login.errors.signInError));
    },
  });

  const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setEmail(e.target.value);
  };

  const handlePasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setPassword(e.target.value);
  };

  const handleEmailLogin = () => {
    if (!email || !password) return;

    gRecaptchaExecute((captchaToken: string) => {
      const input: AuthUserSignInInput = {
        captchaToken,
        attributes: {
          email,
          password,
          rememberMe: false,
          intake: false,
          optional: {
            storeId,
            roleType: "Patient",
          },
        },
      };

      if (storeSlug && journeyPackageId) {
        input.attributes.optional = {
          ...input.attributes.optional,
          redirectPath: `/r/checkout/${storeSlug}/journey/${journeyPackageId}`,
        };
      }

      userSignIn({
        variables: { input },
      });

      void logAnalyticsEvent({
        variables: {
          input: {
            eventName: "journey.checkout.signin.submit",
            payload: {
              "Sign-in method": "Password",
            },
          },
        },
      });
    });
  };

  const renderWelcomeHeader = () => (
    <div>
      <Typography type="h2">{t(l.journeyCheckout.login.welcomeBack)}</Typography>
      <Spacer height="half" />
      <Typography>{t(l.journeyCheckout.login.accountExists)}</Typography>
      <Spacer height="one" />
    </div>
  );

  const renderEmailPasswordForm = () => (
    <div>
      <FloatingLabelInput
        id={FORM_IDS.EMAIL}
        type="email"
        label={t(l.journeyCheckout.login.emailLabel)}
        value={email}
        onChange={handleEmailChange}
        required
        disabled={!!initialEmail} // Disable if email was pre-filled
      />
      <Spacer height="one" />
      <FloatingLabelInput
        id={FORM_IDS.PASSWORD}
        type="password"
        label={t(l.journeyCheckout.login.passwordLabel)}
        value={password}
        onChange={handlePasswordChange}
        required
      />
      <Spacer height="one" />
      <Button
        onClick={handleEmailLogin}
        isFullWidth
        size="large"
        isLoading={isSignInLoading}
        disabled={!email || !password}
      >
        {t(l.journeyCheckout.login.continueButton)}&nbsp;
        <FontAwesomeIcon icon={faChevronRight} />
      </Button>
    </div>
  );

  return (
    <div>
      {!!isMobile && <Spacer height="one" />}
      {renderWelcomeHeader()}
      {renderEmailPasswordForm()}
      <Spacer height="one" />
      <Typography type="footnote" isMarginless css={styles.orSection}>
        {t(l.journeyCheckout.login.or)}
      </Typography>
      <Spacer height="one" />
      <LoginOptions
        commonSSOProps={{
          storeSlug,
          additionalAttributes,
        }}
        hideOrText
      />
      <Spacer height="one" />
      <div>
        <JourneyMagicLinkButton
          isUnauthenticated
          additionalAttributes={additionalAttributes}
          onMagicLinkClick={onMagicLinkClick}
          email={email}
        />
      </div>
    </div>
  );
};

export { JourneyLoginForm };
